// "Confirm it's you" — password typed again before a critical operation
// (security policy, Phase 4). Same file in the admin app, HRMS, kanban and PWA.
//
// How it works, without touching any screen:
// 1. A critical API call answers 428 { detail.code: 'reauth_required' }.
// 2. The axios interceptor below opens this dialog; the password goes to
//    POST /auth/reauth, which returns a re-auth token valid for 5 minutes.
// 3. The original request is sent again with header `x-reauth-token`, and later
//    critical calls in those 5 minutes carry it automatically (no new prompt).
// Cancel → the screen gets the original error, exactly like any failed save.
// A wrong password only shows "Incorrect password" (no account lock).

let cached = null; // { token, until }
let pending = null; // one dialog at a time, shared by parallel requests

export const currentReauthToken = () => (cached && Date.now() < cached.until ? cached.token : null);

const cleanToken = (t) => String(t || "").replace(/^"|"$/g, "");

// Popups (MUI Dialog, react-bootstrap Modal) keep keyboard focus inside themselves:
// a box added outside them can't be typed into. So the box is placed INSIDE the
// top-most open popup (its focus area), or in <body> when none is open.
const dialogHost = () => {
  const candidates = [
    // the popup's own focus area = its DIRECT child with tabindex -1 (not e.g. a disabled button inside)
    ...document.querySelectorAll('.MuiModal-root:not([aria-hidden="true"]) > [tabindex="-1"]'),
    ...document.querySelectorAll(".modal.show"),
  ].filter((el) => el.isConnected && el.getClientRects().length > 0);
  if (!candidates.length) return document.body;
  // last in document order = top-most
  return candidates.sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1)).pop();
};

const openDialog = (check) =>
  new Promise((resolve) => {
    const prevFocus = document.activeElement;
    const backdrop = document.createElement("div");
    backdrop.setAttribute(
      "style",
      "position:fixed;inset:0;background:rgba(15,23,42,.55);z-index:30000;display:flex;align-items:center;justify-content:center;padding:16px;font-family:inherit"
    );
    backdrop.innerHTML = `
      <form role="dialog" aria-modal="true" aria-labelledby="reauth-title" style="background:#fff;border-radius:10px;padding:24px;width:100%;max-width:400px;box-shadow:0 12px 40px rgba(0,0,0,.25);color:#1f2937">
        <h3 id="reauth-title" style="margin:0 0 6px;font-size:18px;font-weight:600">Confirm it's you</h3>
        <p style="margin:0 0 14px;font-size:14px;color:#4b5563">Type the password <strong>you</strong> log in with — not the employee's password and not a PIN.</p>
        <label for="reauth-password" style="display:block;font-size:13px;font-weight:600;margin-bottom:4px">Password</label>
        <input id="reauth-password" type="password" autocomplete="current-password" required
          style="width:100%;box-sizing:border-box;padding:9px 10px;border:1px solid #cbd5e1;border-radius:8px;font-size:14px" />
        <div data-error role="alert" style="min-height:18px;margin-top:6px;font-size:13px;color:#b91c1c"></div>
        <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:12px">
          <button type="button" data-cancel style="border:0;border-radius:8px;padding:8px 16px;font-size:14px;font-weight:600;cursor:pointer;background:#eef2f6;color:#1f2937">Cancel</button>
          <button type="submit" data-ok style="border:0;border-radius:8px;padding:8px 16px;font-size:14px;font-weight:600;cursor:pointer;background:#1a7d3e;color:#fff">Confirm</button>
        </div>
      </form>`;
    dialogHost().appendChild(backdrop);
    // clicks inside the box must not reach the popup underneath (backdrop-click closes some popups)
    backdrop.addEventListener("click", (e) => e.stopPropagation());
    backdrop.addEventListener("mousedown", (e) => e.stopPropagation());
    const form = backdrop.querySelector("form");
    const input = backdrop.querySelector("input");
    const errorBox = backdrop.querySelector("[data-error]");
    const ok = backdrop.querySelector("[data-ok]");
    const cancel = backdrop.querySelector("[data-cancel]");

    const close = (result) => {
      document.removeEventListener("keydown", onKey, true);
      backdrop.remove();
      if (prevFocus && prevFocus.focus) prevFocus.focus();
      resolve(result);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        close(false);
      }
    };
    document.addEventListener("keydown", onKey, true);
    cancel.addEventListener("click", () => close(false));
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      if (!input.value) return;
      ok.disabled = true;
      ok.textContent = "Checking…";
      errorBox.textContent = "";
      const result = await check(input.value);
      if (result.ok) return close(true);
      ok.disabled = false;
      ok.textContent = "Confirm";
      errorBox.textContent = result.message || "Incorrect password";
      input.value = "";
      input.focus();
      if (result.fatal) {
        ok.disabled = true;
        cancel.disabled = true;
        setTimeout(() => {
          close(false);
          result.onFatal && result.onFatal();
        }, 2500);
      }
    });
    setTimeout(() => input.focus(), 0);
  });

/**
 * Ask for the password. Resolves true when a fresh re-auth token is cached.
 * opts: { baseUrl, getToken, getCompanyId, onLocked }
 */
export const requestReauth = (opts) => {
  if (pending) return pending;
  pending = openDialog(async (password) => {
    try {
      const headers = { "Content-Type": "application/json", Authorization: `Bearer ${cleanToken(opts.getToken())}` };
      const companyId = opts.getCompanyId && opts.getCompanyId();
      if (companyId) headers["company-id"] = companyId;
      const res = await fetch(`${opts.baseUrl}/auth/reauth`, { method: "POST", headers, body: JSON.stringify({ password }) });
      let body = {};
      try {
        body = await res.json();
      } catch (e) {
        /* empty */
      }
      if (res.ok && body.data && body.data.reauthToken) {
        // renew a little early so a token never expires mid-request
        cached = { token: body.data.reauthToken, until: Date.now() + Math.max(30, (body.data.expiresInSeconds || 300) - 20) * 1000 };
        return { ok: true };
      }
      if (res.status === 423 || res.status === 401) {
        return { ok: false, fatal: true, message: body.message || "Your session has ended.", onFatal: opts.onLocked };
      }
      return { ok: false, message: body.message || "Incorrect password" };
    } catch (e) {
      return { ok: false, message: "Could not reach the server. Try again." };
    }
  }).finally(() => {
    pending = null;
  });
  return pending;
};

let installed = false;

/** Hook the dialog into an axios instance (call once). */
export const installReauthInterceptor = (axiosInstance, opts) => {
  if (installed) return;
  installed = true;
  const isOurApi = (config) => {
    const url = String((config && config.url) || "");
    return !/^https?:\/\//i.test(url) || url.startsWith(opts.baseUrl);
  };
  axiosInstance.interceptors.request.use((config) => {
    const t = currentReauthToken();
    if (t && isOurApi(config)) {
      config.headers = config.headers || {};
      if (!config.headers["x-reauth-token"]) config.headers["x-reauth-token"] = t;
    }
    return config;
  });
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const res = error && error.response;
      const code = res && res.data && res.data.detail && res.data.detail.code;
      if (!res || res.status !== 428 || code !== "reauth_required" || !error.config || error.config.__reauthRetried) {
        return Promise.reject(error);
      }
      const confirmed = await requestReauth(opts);
      if (!confirmed) return Promise.reject(error);
      error.config.__reauthRetried = true;
      error.config.headers = error.config.headers || {};
      error.config.headers["x-reauth-token"] = currentReauthToken();
      return axiosInstance(error.config);
    }
  );
};
